import React, { useCallback, useEffect, useState } from "react";
import { ArrowUpRight, X } from "lucide-react";
import { toast } from "sonner";
import { agencyApi, agencyToken, formatApiError } from "@/lib/api";
import { Avatar } from "@/components/shared";

const POLL_MS = 30000;
const timeAgo = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 1 ? "now" : m < 60 ? `${m}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`;
};

function BubblePanel({ b, onClose }) {
  const [going, setGoing] = useState(false);
  const viewFull = async () => {
    setGoing(true);
    try {
      const { data } = await agencyApi.post("/auth/enter-workspace", { workspace_id: b.workspace_id });
      localStorage.setItem("mc_ws_token", data.token);
      window.location.assign(`/${data.slug}/comms?c=${b.conversation_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setGoing(false); }
  };
  return (
    <div data-testid={`comms-bubble-panel-${b.conversation_id}`} className="absolute bottom-0 right-[72px] w-[340px] rounded-2xl border border-[#EEEFEF] bg-white shadow-[0_24px_60px_-20px_rgba(6,6,12,0.35)] overflow-hidden animate-in fade-in slide-in-from-right-2 duration-200">
      <div className="flex items-start gap-3 px-4 py-3 border-b border-[#f0f0f3]">
        <Avatar name={b.author?.name} size={34} account="client" userId={b.author?.id} hasAvatar={b.author?.has_avatar} version={b.author?.avatar_version} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-[#06060C] truncate">{b.author?.name}</p>
          <p className="text-[11px] text-[#9FA1A4] truncate">{b.workspace_name} · {b.subject}</p>
        </div>
        <button data-testid="comms-bubble-close" onClick={onClose} className="h-7 w-7 rounded-full flex items-center justify-center text-[#9FA1A4] hover:bg-[#F6F6FD] hover:text-[#06060C]"><X className="h-4 w-4" /></button>
      </div>
      <div className="max-h-[320px] overflow-y-auto px-4 py-3 space-y-3 bg-[#FAFAFC]">
        {b.messages.map((m) => {
          const team = m.author_type === "team";
          return (
            <div key={m.id} data-testid={`comms-bubble-msg-${m.id}`} className={`flex gap-2 ${team ? "flex-row-reverse" : ""}`}>
              <Avatar name={m.author_name} size={24} account={team ? "agency" : "client"} userId={m.author_id} hasAvatar={m.has_avatar} version={m.avatar_version} />
              <div className={`max-w-[78%] ${team ? "items-end" : "items-start"} flex flex-col`}>
                <div className={`rounded-2xl px-3 py-2 text-[13px] leading-snug whitespace-pre-wrap break-words ${team ? "bg-[#06060C] text-white rounded-tr-md" : "bg-white border border-[#EEEFEF] text-[#06060C] rounded-tl-md"}`}>
                  {m.gif_url ? <img src={m.gif_url} alt="GIF" className="rounded-lg max-h-32" /> : (m.body || "").slice(0, 600)}
                </div>
                <span className="text-[10px] text-[#9FA1A4] mt-0.5 px-1">{team ? "" : `${m.author_name} · `}{timeAgo(m.created_at)}</span>
              </div>
            </div>
          );
        })}
      </div>
      <button data-testid="comms-bubble-view-full" onClick={viewFull} disabled={going} className="w-full flex items-center justify-center gap-1.5 py-3 font-mono-data text-[11px] uppercase tracking-[0.18em] text-[#723FFF] hover:bg-[#F6F6FD] border-t border-[#f0f0f3] transition-colors">
        {going ? "Opening…" : "View full comms"} <ArrowUpRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function CommsBubbles() {
  const [bubbles, setBubbles] = useState([]);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    if (!agencyToken() || document.hidden) return;
    agencyApi.get("/agency/comms-bubbles").then(({ data }) => setBubbles((prev) => {
      const open = prev.find((b) => b.conversation_id === openId);
      return open && !data.bubbles.some((b) => b.conversation_id === openId) ? [open, ...data.bubbles] : data.bubbles;
    })).catch(() => {});
  }, [openId]);
  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    document.addEventListener("visibilitychange", load);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", load); };
  }, [load]);

  const open = (b) => {
    if (openId === b.conversation_id) { close(); return; }
    if (openId) setBubbles((xs) => xs.filter((x) => x.conversation_id !== openId));
    setOpenId(b.conversation_id);
    agencyApi.post(`/agency/comms-bubbles/${b.conversation_id}/read`).catch(() => {});
  };
  const close = () => { setBubbles((xs) => xs.filter((x) => x.conversation_id !== openId)); setOpenId(null); };

  if (!bubbles.length) return null;
  return (
    <div data-testid="comms-bubbles" className="fixed bottom-6 right-6 z-40 flex flex-col-reverse gap-3">
      {bubbles.map((b, i) => (
        <div key={b.conversation_id} className="relative animate-in fade-in zoom-in-75 duration-300" style={{ animationDelay: `${i * 60}ms` }}>
          {openId === b.conversation_id && <BubblePanel b={b} onClose={close} />}
          <button data-testid={`comms-bubble-${b.conversation_id}`} onClick={() => open(b)} title={`${b.author?.name} · ${b.subject}`}
            className={`relative h-14 w-14 rounded-full p-[3px] bg-white shadow-[0_10px_30px_-8px_rgba(6,6,12,0.4)] hover:scale-105 transition-transform ${openId === b.conversation_id ? "ring-2 ring-[#723FFF]" : ""}`}>
            <Avatar name={b.author?.name} size={50} account="client" userId={b.author?.id} hasAvatar={b.author?.has_avatar} version={b.author?.avatar_version} />
            {openId !== b.conversation_id && <span data-testid={`comms-bubble-count-${b.conversation_id}`} className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-[#E5484D] border-2 border-white text-white text-[10px] font-semibold flex items-center justify-center">{b.unread > 9 ? "9+" : b.unread}</span>}
          </button>
        </div>
      ))}
    </div>
  );
}
